import { useState, useEffect, useCallback } from "react";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

export type CustomerSession = {
    clientSecret: string;
    publishableKey: string;
    pricingTableId: string;
    expiresAt: number;
};

type UseCustomerSessionOptions = {
    isPaymentFeatureEnabled?: boolean;
};

type UseCustomerSessionResult = {
    customerSession: CustomerSession | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    isExpired: boolean;
};

export const useCustomerSession = (options?: UseCustomerSessionOptions): UseCustomerSessionResult => {
    const { selectedCompany } = useFoodpilotContext();
    const companyId = selectedCompany?.id;
    const isPaymentFeatureEnabled = options?.isPaymentFeatureEnabled ?? false;
    const canFetchSession = isPaymentFeatureEnabled && !!companyId;

    const [customerSession, setCustomerSession] = useState<CustomerSession | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchCustomerSession = useCallback(async (): Promise<void> => {
        if (!canFetchSession || !companyId) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await loggedInApiCall.get(`/api/organizations/${companyId}/payment/customer-session`);

            if (
                !response.data?.clientSecret ||
                !response.data?.publishableKey ||
                !response.data?.pricingTableId ||
                !response.data?.expiresAt
            ) {
                throw new Error("Invalid customer session data received");
            }

            setCustomerSession(response.data);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to fetch customer session";
            setError(errorMessage);
            console.error("Error fetching customer session:", err);
        } finally {
            setLoading(false);
        }
    }, [canFetchSession, companyId]);

    const refresh = useCallback(async (): Promise<void> => {
        if (!canFetchSession) return;
        await fetchCustomerSession();
    }, [canFetchSession, fetchCustomerSession]);

    const isExpired = customerSession ? Date.now() / 1000 > customerSession.expiresAt : false;

    useEffect(() => {
        if (!isPaymentFeatureEnabled) {
            setCustomerSession(null);
            setError(null);
            setLoading(false);
            return;
        }

        if (!companyId) {
            setCustomerSession(null);
            setError("No company selected");
            setLoading(false);
            return;
        }

        fetchCustomerSession();
    }, [isPaymentFeatureEnabled, companyId, fetchCustomerSession]);

    // Auto-refresh 5 minutes before expiration
    useEffect(() => {
        if (!canFetchSession || !customerSession) return;

        const timeUntilExpiry = customerSession.expiresAt * 1000 - Date.now();
        const refreshTime = Math.max(timeUntilExpiry - 300000, 0); // 5 min before

        if (refreshTime > 0) {
            const timeout = setTimeout(() => {
                refresh();
            }, refreshTime);

            return () => clearTimeout(timeout);
        }
    }, [canFetchSession, customerSession, refresh]);

    return {
        customerSession,
        loading,
        error,
        refresh,
        isExpired,
    };
};
